import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
  Button,
} from '@mui/material';
import { DirectionsRun, LocationOn, CalendarToday } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';

const contentUseCase = new ContentUseCase();

export const TimelinePage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { runningEvents, runningEventsPage, fundraiserInfo } = contentUseCase.getAllContent(language);

  // Runs of the current season come first; earlier years are shown below as already run
  const upcomingEvents = runningEvents.filter((event) =>
    event.date.includes(fundraiserInfo.stats.runsYear)
  );
  const pastEvents = runningEvents.filter(
    (event) => !event.date.includes(fundraiserInfo.stats.runsYear)
  );

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="timeline" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(2) }}>
        {runningEventsPage.title}{' '}
        <Box
          component="img"
          src="/Light_blue_ribbon.svg.png"
          alt="Light Blue Awareness Ribbon"
          sx={{
            height: '60px',
            width: 'auto',
            marginLeft: theme.spacing(2),
            verticalAlign: 'middle',
          }}
        />
      </Typography>

      <Typography variant="h5" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(2) }}>
        {runningEventsPage.subtitle}
      </Typography>

      {runningEventsPage.description && (
        <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(8), maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
          {runningEventsPage.description}
        </Typography>
      )}

      <Typography variant="h4" component="h2" align="center" sx={{ mb: theme.spacing(4) }}>
        {runningEventsPage.upcomingTitle}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: theme.spacing(3),
          mb: theme.spacing(8),
        }}
      >
        {upcomingEvents.map((event) => (
          <Card
            key={`run-${event.name}-${event.date}`}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: theme.spacing(4), flexGrow: 1 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
                  border: `1px solid ${theme.palette.primary.main}20`,
                  mb: theme.spacing(3),
                }}
              >
                <DirectionsRun sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: theme.spacing(2),
                }}
              >
                {event.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), mb: theme.spacing(1.5) }}>
                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {event.date}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {event.location}
                </Typography>
              </Box>
              {event.distances && (
                <Typography
                  variant="body2"
                  color='text.secondary'
                  sx={{
                    mt: theme.spacing(2),
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {event.distances}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {pastEvents.length > 0 && (
        <>
          <Typography variant="h4" component="h2" align="center" sx={{ mb: theme.spacing(4) }}>
            {runningEventsPage.pastTitle}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: theme.spacing(3),
              mb: theme.spacing(8),
            }}
          >
            {pastEvents.map((event) => (
              <Card
                key={`past-run-${event.name}-${event.date}`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ p: theme.spacing(4), flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
                      border: `1px solid ${theme.palette.primary.main}20`,
                      mb: theme.spacing(3),
                    }}
                  >
                    <DirectionsRun sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: theme.spacing(2),
                    }}
                  >
                    {event.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), mb: theme.spacing(1.5) }}>
                    <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {event.date}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {event.location}
                    </Typography>
                  </Box>
                  {event.distances && (
                    <Typography
                      variant="body2"
                      color='text.secondary'
                      sx={{
                        mt: theme.spacing(2),
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      {event.distances}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(4),
      }} />

      <Box sx={{ textAlign: 'center', mt: theme.spacing(4) }}>
        <Typography variant="h4" gutterBottom>
          {runningEventsPage.callToAction.title}
        </Typography>
        <Typography variant="h6" paragraph color="text.secondary" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {runningEventsPage.callToAction.description}
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: theme.spacing(2) }}>
          {runningEventsPage.callToAction.additionalInfo}
        </Typography>
        {runningEventsPage.callToAction.contactInfo && (
          <Typography variant="body1" color="primary" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', fontWeight: 500, mb: theme.spacing(4) }}>
            {runningEventsPage.callToAction.contactInfo}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          href={`mailto:runforme.contact@googlemail.com?subject=${encodeURIComponent(language === 'de' ? 'Mitmachen bei Run for M.E.' : 'Join Run for M.E.')}`}
          sx={{ px: theme.spacing(6), py: theme.spacing(2) }}
        >
          {language === 'de' ? 'Kontaktiere uns' : 'Contact Us'}
        </Button>
      </Box>
      <BackToTop />
    </Container>
  );
};
